import {
	Portal,
	RewriteToolsMenu,
	RichTextEditorContent,
	RichTextEditorContentHandle,
	RichTextEditorIntermediateStateHandlers,
	RichTextEditorRewriteToolsResult,
	RichTextEditorToolbar,
	useRichTextEditorIntermediateState,
	useRichTextEditorRewriteTools,
	ValidationError
} from "client/Components";
import { ParagraphElementSaveModel } from "client/Api/Hub/common.composer";
import { IObjectInputAttachable } from "client/Forms/attachable";
import { getClientId, hasClientId } from "client/Misc/clientId";
import { localize } from "client_legacy/ndaw/culture";
import { Guid } from "client_legacy/ndaw/guid";
import {
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useImperativeHandle,
	useRef,
	useState
} from "react";
import { useBuilderEditor } from "../useBuilderEditor";
import { userMentionType } from "client/Components/MentionInput/converters";
import { DropOverMentionSuggestionBox } from "../../MentionSuggestionBox/DropOverMentionSuggestionBox";
import {
	ensureNotNil,
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { findUsers } from "client/Api/Hub/hub";
import { generateUserDetailsUrl } from "../misc";
import { ElementLayout } from "./ElementLayout";
import { ElementGenericActionHandlers, ElementGenericHandle } from "./Element";
import { useIsTouchDevice } from "client/Hooks";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { InlineActionList } from "../Actions/InlineActionList";
import { RewriteToolEditorKind, useRewriteToolsService } from "client/Features/ChatAssistant/useRewriteToolsService";
import { hubItemKindToRewriteToolTargetKindMap } from "../../common";
import { useIsAiAssistantAvailable } from "client/Features/ChatAssistant/useIsAiAssistantAvailable";

export type ParagraphProps = {
	attachable: IObjectInputAttachable<ParagraphElementSaveModel>;
	genericHandlers: ElementGenericActionHandlers;
	ref: Ref<ElementGenericHandle>;
	hubItemKind: HubItemKind;
};

export const Paragraph = (props: ParagraphProps): ReactElement => {
	const {
		attachable,
		genericHandlers,
		ref,
		hubItemKind
	} = props;

	const { value } = attachable;

	const {
		onAddNewElementToColumn,
		onOpenEmbedInputDialog,
		onOpenVideoInputDialog,
		onSelectImage,
		onMoveUp,
		onMoveDown,
		onDuplicate,
		onDelete
	} = genericHandlers;

	const [isImproveTextMenuOpen, setIsImproveTextMenuOpen] = useState<boolean>(false);

	const isAiAssistantAvailable: boolean = useIsAiAssistantAvailable();
	const isTouchDevice: boolean = useIsTouchDevice();

	const rootRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const contentRef: RefObject<RichTextEditorContentHandle | null> = useRef<RichTextEditorContentHandle>(null);
	const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const improveTextButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const richTextEditorIntermediateState: RichTextEditorIntermediateStateHandlers = useRichTextEditorIntermediateState();

	const getHasChangesHandler = useCallback(
		(): boolean => {
			const content: RichTextEditorContentHandle | null = contentRef.current;
			return isNotNil(content) && content.getUndoDepth() > 0;
		},
		[]
	);

	const {
		activate,
		isActive,
		toolbarContainer
	} = useBuilderEditor({
		getHasChanges: getHasChangesHandler
	});

	const focusInputHandler = useCallback(
		(): void => {
			contentRef.current
				?.focus();
		},
		[]
	);

	const rewriteToolsResult: RichTextEditorRewriteToolsResult = useRichTextEditorRewriteTools(
		richTextEditorIntermediateState.intermediateState,
		richTextEditorIntermediateState.setIntermediateState,
		focusInputHandler,
		{
			kind: "selection",
			shouldUseEntireDocumentWhenEmpty: true
		},
		useRewriteToolsService(
			hubItemKindToRewriteToolTargetKindMap[hubItemKind],
			RewriteToolEditorKind.RichText
		)
	);

	useImperativeHandle(
		ref,
		(): ElementGenericHandle => ({
			findAndFocus: (clientId: Guid): void => {
				if (hasClientId(value) && getClientId(value) === clientId) {
					focusInputHandler();
				}
			},
			scrollIntoView: (): void => {
				ensureNotNil(rootRef.current)
					.scrollIntoView();
			}
		}),
		[value, focusInputHandler]
	);

	const findUsersHandler = useCallback(
		(
			searchTerm: string,
			amount: number,
			signal: AbortSignal
		): Promise<readonly UserIdentityModel[]> => {
			return findUsers(
				searchTerm,
				amount,
				signal
			);
		},
		[]
	);

	const closeRewriteToolsMenu = useCallback(
		(): void => {
			setIsImproveTextMenuOpen(false);
		},
		[]
	);

	return (
		<ElementLayout
			hasError={!attachable.validation.isValid()}
			trackingId={attachable.reducer.path}
			ref={rootRef}
			onCloseAllMenus={closeRewriteToolsMenu}
		>
			<ElementLayout.ActionsMenu trackingId="element-actions">
				<ElementLayout.AddElementMenuItem
					onAddNewElementToColumn={onAddNewElementToColumn}
					onOpenEmbedInputDialog={onOpenEmbedInputDialog}
					onOpenVideoInputDialog={onOpenVideoInputDialog}
					onSelectImage={onSelectImage}
					trackingId={generateTrackingId(attachable.reducer.path, "__add-element-menu")}
				/>
				{onMoveUp && (
					<ElementLayout.MoveUpMenuItem
						onClick={onMoveUp}
					/>
				)}
				{onMoveDown && (
					<ElementLayout.MoveDownMenuItem
						onClick={onMoveDown}
					/>
				)}
				<ElementLayout.DuplicateMenuItem
					onClick={onDuplicate}
				/>
				<ElementLayout.DeleteMenuItem
					onClick={onDelete}
				/>
				{isAiAssistantAvailable
					&& !isTouchDevice
					&& rewriteToolsResult.canRewrite
					&& (
						<>
							<InlineActionList.Divider />
							<ElementLayout.ImproveTextMenuItem
								onClick={() => setIsImproveTextMenuOpen(true)}
								ref={improveTextButtonRef}
							/>
							{isImproveTextMenuOpen && (
								<RewriteToolsMenu
									handlers={rewriteToolsResult.handlers}
									onClose={closeRewriteToolsMenu}
									target={ensureNotNil(improveTextButtonRef.current)}
									trackingId="improve-text__drop-over"
								/>
							)}
						</>
					)
				}
			</ElementLayout.ActionsMenu>
			<div ref={containerRef}>
				{isActive && isNotNil(toolbarContainer) && (
					<Portal container={toolbarContainer}>
						<RichTextEditorToolbar
							{...attachable.for("html")
								.attachToRichTextEditorToolbar(richTextEditorIntermediateState)
							}
							onFocusInput={focusInputHandler}
							isBorderless
							hasNoPadding
							areHashUrlsAllowed
						/>
					</Portal>
				)}
				<RichTextEditorContent
					{...attachable.for("html")
						.attachToRichTextEditorContent(richTextEditorIntermediateState)}
					placeholder={localize("_EnterDescription")}
					onFocus={activate}
					ref={contentRef}
					height="auto"
					isBorderless
					hasNoPadding
					hasNoBackgroundColor
					mentionTypes={{
						[userMentionType]: {
							kind: "insert-anchor",
							hrefPattern: generateUserDetailsUrl
						}
					}}
					renderSuggestionBox={
						(props, ref, rect) => (
							<DropOverMentionSuggestionBox
								{...props}
								ref={ref}
								target={ensureNotNil(containerRef.current)}
								findUsers={findUsersHandler}
								rect={rect}
							/>
						)
					}
				/>
			</div>
			<ValidationError {...attachable.for("html").attachToValidationError()} />
		</ElementLayout>
	);
};
Paragraph.displayName = "Paragraph";
